import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { PageHeader } from '../../../components/PageHeader';
import DashboardLayout from '../../Dashboards/DashboardLayout';
import ReadingsWidget, { WidgetTypes } from '../../Dashboards/Widgets/ReadingsWidget';
import actions from './redux/actions';
import { NONE_ASSIGNEE_FILTER } from './shared';
import { SuperuserHealthTabs } from './SuperuserHealthCenter';

export const DASHBOARD_ID = 'superuserHealthDashboard';

export const WidgetMetadata = [
  {
    id: 'myOpenIssues',
    name: 'My open issues',
    permission: '',
    defProps: { i: 'myOpenIssues', w: 4, h: 2, minW: 3, minH: 2 },
  },
  {
    id: 'allOpenIssues',
    name: 'All open issues',
    permission: '',
    defProps: { i: 'allOpenIssues', w: 4, h: 2, minW: 3, minH: 2 },
  },
  {
    id: 'unassignedOpenIssues',
    name: 'Unassigned open issues',
    permission: '',
    defProps: { i: 'unassignedOpenIssues', w: 4, h: 2, minW: 3, minH: 2 },
  },
];

export const DefaultLayouts = {
  l: [
    { ...WidgetMetadata[0].defProps, x: 0, y: 0 },
    { ...WidgetMetadata[1].defProps, x: 4, y: 0 },
    { ...WidgetMetadata[2].defProps, x: 8, y: 0 },
  ],
  m: [
    { ...WidgetMetadata[0].defProps, x: 0, y: 0, w: 4 },
    { ...WidgetMetadata[1].defProps, x: 4, y: 0, w: 4 },
    { ...WidgetMetadata[2].defProps, x: 0, y: 2, w: 8 },
  ],
  s: [
    { ...WidgetMetadata[0].defProps, x: 0, y: 0, w: 4 },
    { ...WidgetMetadata[1].defProps, x: 0, y: 2, w: 4 },
    { ...WidgetMetadata[2].defProps, x: 0, y: 4, w: 4 },
  ],
};

export const getWidgetsMetadata = () => WidgetMetadata;

export const getDefaultLayouts = () => DefaultLayouts;

function isUnassignedIssue(issue) {
  return !issue?.assigned_to || !String(issue.assigned_to).trim();
}

function DashboardOverview({ getIssues, onNavigate, username }) {
  const [counts, setCounts] = React.useState({
    myOpen: 0,
    openTotal: 0,
    openUnassigned: 0,
  });

  React.useEffect(() => {
    getIssues({
      limit: 1000,
      resolved: false,
      sortColumn: 'created_at',
      sortType: 'descending',
    }).then(resp => {
      const issues = Array.isArray(resp?.response) ? resp.response : [];
      setCounts({
        myOpen: username ? issues.filter(issue => issue?.assigned_to === username).length : 0,
        openTotal: issues.length,
        openUnassigned: issues.filter(isUnassignedIssue).length,
      });
    });
  }, [getIssues, username]);

  const widgets = React.useMemo(
    () => [
      {
        ...WidgetMetadata[0],
        noPadding: true,
        noBorder: true,
        render: (
          <ReadingsWidget
            icon="alert"
            color="orange"
            title="My open issues"
            value={counts.myOpen}
            type={WidgetTypes.colorfullWithIcon}
            onClick={() => onNavigate(`${SuperuserHealthTabs.Issues.path}?assignedTo=${encodeURIComponent(username || '')}&resolved=false`)}
          />
        ),
      },
      {
        ...WidgetMetadata[1],
        noPadding: true,
        noBorder: true,
        render: (
          <ReadingsWidget
            icon="alert"
            color="green"
            title="All open issues"
            value={counts.openTotal}
            type={WidgetTypes.colorfullWithIcon}
            onClick={() => onNavigate(`${SuperuserHealthTabs.Issues.path}?resolved=false`)}
          />
        ),
      },
      {
        ...WidgetMetadata[2],
        noPadding: true,
        noBorder: true,
        render: (
          <ReadingsWidget
            icon="alert"
            color="pink"
            title="Unassigned open issues"
            value={counts.openUnassigned}
            type={WidgetTypes.colorfullWithIcon}
            onClick={() => onNavigate(`${SuperuserHealthTabs.Issues.path}?assignedTo=${encodeURIComponent(NONE_ASSIGNEE_FILTER)}&resolved=false`)}
          />
        ),
      },
    ],
    [counts.myOpen, counts.openTotal, counts.openUnassigned, onNavigate, username],
  );

  return (
    <>
      <PageHeader left="Dashboard" />
      <DashboardLayout
        dashboardId={DASHBOARD_ID}
        widgets={widgets}
        isReadOnly
        rowHeight={90}
      />
    </>
  );
}

DashboardOverview.propTypes = {
  getIssues: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  username: PropTypes.string,
};

const mapStateToProps = state => ({
  username: state.auth?.profile?.username || '',
});

const mapDispatchToProps = dispatch => ({
  getIssues: params => dispatch(actions.getIssues(params)),
  onNavigate: path => dispatch(push(path)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DashboardOverview);
